import firebase from "firebase/compat/app";
import {db, firebaseApp} from "../../firebase";
import {Collections} from "../collections";
import {getUid, updateUserDisplayName} from "../auth";
import {CompanyRmI, CompanyRmPartialI} from "../../db/models/company/rm";
import {CompanyInterface} from "../../db/models/company/company";
import {CompanyAreaI, CompanyAreaPartialI,} from "../../db/models/company/area";
import {MissingType} from "../../db/models/extra";
import {CompanyGameInterface} from "../../db/models/company/game";
import {CompanyGameSubmissionType} from "../../db/models/company/gameSubmission";
import "firebase/compat/functions";

export const getCompany = () => {
    const uid = getUid();
    if (!uid) return Promise.reject("no uid");

    return db
        .collection(Collections.USERS)
        .doc(uid)
        .get()
        .then((doc) => {
            if (doc.exists) return doc.data();
            else return null;
        });
};

export const updateCompany = async (
    company: CompanyInterface,
    updateDisplayName: boolean
) => {
    const uid = getUid();
    if (!uid || !company) return Promise.reject("no uid");

    try {
        // update company on db
        await db.collection(Collections.USERS).doc(uid).update(company);

        // if company name changed, update it in account info
        if (updateDisplayName) await updateUserDisplayName(company.name);
    } catch (error) {
        return Promise.reject();
    }
};

export const updateRmSoftSkills = (rmId: string, data: MissingType) => {
    if (!rmId || !data) return Promise.reject("bad params");

    return db.collection(Collections.COMPANY_RMS).doc(rmId).update(data);
};

export const getCompanyListener = (observer: any) => {
    const uid = getUid();
    if (!uid) return;

    return db.collection(Collections.USERS).doc(uid).onSnapshot(observer);
};

export const getRmListener = (observer: any, rmId: string) => {
    if (!rmId) return;

    return db.collection(Collections.COMPANY_RMS).doc(rmId).onSnapshot(observer);
};

export const getRm = (rmId: string) => {
    if (!rmId) return null;

    return db
        .collection(Collections.COMPANY_RMS)
        .doc(rmId)
        .get()
        .then((doc) => {
            if (doc.exists) return doc.data() as CompanyRmI;
            else return null;
        });
};

export const getPublicRmById = (rmId: string) => {
    if (!rmId) return Promise.resolve(null);

    return db
        .collection(Collections.COMPANY_RMS)
        .where("id", "==", rmId)
        .where("demo", "==", false)
        .get()
        .then((snapshot) => {
            let res: CompanyRmI[] = [];
            snapshot.forEach((doc) => res.push(doc.data() as CompanyRmI));
            if (res.length !== 1) return null;
            else return Promise.resolve(res[0]);
        })
        .catch(function (error) {
            return Promise.resolve(null);
        });
};

export const getCompanyById = (companyId: string) => {
    if (!companyId) return Promise.resolve(null);

    return db
        .collection(Collections.USERS)
        .doc(companyId)
        .get()
        .then((doc) => {
            if (doc.exists) return doc.data() as CompanyInterface;
            else return null;
        });
};

export const getRmsById = async (rmIds: string[], callback: Function) => {
    if (!rmIds) return Promise.reject(null);

    let rmRefs = rmIds.map((rmId) =>
        db.collection(Collections.COMPANY_RMS).doc(rmId).get()
    );

    Promise.all(rmRefs).then((docs) => {
        const rms = docs.map((doc) => doc.data());
        const filteredRms = rms.filter((rm) => rm);
        callback(filteredRms);
    });
};

export const getCompanyAreasListener = (observer: any) => {
    const uid = getUid();
    if (!uid) return;

    return db
        .collection(Collections.COMPANY_AREAS)
        .where("companyId", "==", uid)
        .onSnapshot(observer);
};

export const getCompanyAreas = (companyId: string) => {
    if (!companyId) return Promise.reject();
    return db
        .collection(Collections.COMPANY_AREAS)
        .where("companyId", "==", companyId)
        .get()
        .then((snapshot) => {
            let res: CompanyAreaI[] = [];
            snapshot.forEach((doc) => res.push(doc.data() as CompanyAreaI));
            return Promise.resolve(res);
        })
        .catch(function (error) {
            return Promise.reject(error);
        });
};

export const getRmsByPopularity = () => {
    return db
        .collection(Collections.COMPANY_RMS)
        .where("demo", "!=", true)
        .get()
        .then((snapshot) => {
            if (!snapshot.docs) return [];
            const rms: CompanyRmI[] = snapshot.docs.map(
                (doc) => doc.data() as CompanyRmI
            );
            return rms;
        });
};

export const getAllRmsByPopularity = () => {
    return db
        .collection(Collections.COMPANY_RMS)
        .get()
        .then((snapshot) => {
            if (!snapshot.docs) return [];
            const rms: CompanyRmI[] = snapshot.docs.map(
                (doc) => doc.data() as CompanyRmI
            );
            return rms;
        });
};

// export const getCompanyAreaRmsSnapshot = (areaId, observer) => {
// 	const uid = getUid();
// 	if (!uid) return Promise.reject("no uid");

// 	return db
// 		.collection(Collections.COMPANY_RMS)
// 		.where("areaId", "==", areaId)
// 		.orderBy("created")
// 		.onSnapshot(observer);
// };

export const editCompanyArea = async (newArea: CompanyAreaI) => {
    if (!newArea) return Promise.reject("no newArea");

    const uid = getUid();
    if (!uid) return Promise.reject("no uid");

    const ref = newArea?.id
        ? db.collection(Collections.COMPANY_AREAS).doc(newArea.id)
        : db.collection(Collections.COMPANY_AREAS).doc();
    let area: CompanyAreaI = {
        ...newArea,
    };
    // if new area
    if (!newArea?.id) {
        area = {
            ...area,
            companyId: uid,
            rms: {},
            id: ref.id,
            created: firebase.firestore.FieldValue.serverTimestamp(),
        };
    }

    const areaDoc = Object.assign({}, area);

    await ref.set(areaDoc, {merge: true});
    return Promise.resolve(area);
};

export const addCompanyArea = async (partialArea: CompanyAreaPartialI) => {
    if (!partialArea) return Promise.reject("no partialArea");

    const uid = getUid();
    if (!uid) return Promise.reject("no uid");

    const areaRef = db.collection(Collections.COMPANY_AREAS).doc();

    let area: CompanyAreaI = {
        ...partialArea,
        id: areaRef.id,
        companyId: uid,
        rms: {},
        created: firebase.firestore.FieldValue.serverTimestamp(),
    };

    const areaDoc = Object.assign({}, area);

    try {
        await areaRef.set(areaDoc);
        return Promise.resolve(area);
    } catch (error) {
        return Promise.reject("Area non creata");
    }
};

export const deleteCompanyRm = async (rmId: string, areaId: string) => {
    if (!rmId || !areaId) return;

    const uid: string | null = getUid();

    if (!uid) return Promise.reject("Missing uid");

    //delete games
    let snapshot = await db
        .collection(Collections.GAMES)
        .where("rmId", "==", rmId)
        .get();
    let batch = db.batch();
    snapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
    });
    await batch.commit();

    //delete rm
    batch = db.batch();
    const areaRef = db.collection(Collections.COMPANY_AREAS).doc(areaId);

    const rmRef = db.collection(Collections.COMPANY_RMS).doc(rmId);

    //delete rm on area
    batch.delete(rmRef);
    batch.set(
        areaRef,
        {
            rms: {
                [rmId]: firebase.firestore.FieldValue.delete(),
            },
        },
        {merge: true}
    );

    //decrement rm count on company
    const companyRef = db.collection(Collections.USERS).doc(uid);
    batch.update(companyRef, {
        rmCount: firebase.firestore.FieldValue.increment(-1),
    });

    return batch.commit();
};

export const deleteArea = async (area: CompanyAreaI) => {
    if (!area) return;

    //delete games
    let snapshot = await db
        .collection(Collections.GAMES)
        .where("areaId", "==", area.id)
        .get();
    let batch = db.batch();
    snapshot.docs.forEach((doc) => {
        if (doc.exists) batch.delete(doc.ref);
    });
    await batch.commit();

    //delete rms
    batch = db.batch();
    const areaRef = db.collection(Collections.COMPANY_AREAS).doc(area.id);

    Object.keys(area?.rms || {}).forEach(async (rmId) => {
        const rmRef = db.collection(Collections.COMPANY_RMS).doc(rmId);
        batch.delete(rmRef);
    });

    batch.delete(areaRef);

    return batch.commit();
};

export const addCompanyRm = async (
    partialRm: CompanyRmPartialI,
    areaId: string
) => {
    if (!partialRm || !areaId) return Promise.reject("bad params");

    const uid = getUid();
    if (!uid) return Promise.reject("no uid");

    let batch = db.batch();
    const companyRef = db.collection(Collections.USERS).doc(uid);
    const areaRef = db.collection(Collections.COMPANY_AREAS).doc(areaId);
    const rmRef = db.collection(Collections.COMPANY_RMS).doc();

    const newRm: CompanyRmI = {
        id: rmRef.id,
        companyId: uid,
        areaId,
        followerCount: 0,
        ...partialRm,
    };

    batch.set(rmRef, newRm);
    batch.set(
        areaRef,
        {
            rms: {
                [rmRef.id]: {
                    id: rmRef.id,
                    name: partialRm.name
                },
            },
        },
        {merge: true}
    );
    batch.update(companyRef, {
        rmCount: firebase.firestore.FieldValue.increment(1),
    });

    return batch.commit().then(() => rmRef.id);
};

export const updateRmInfo = (
    rmId: string,
    areaId: string,
    data: CompanyRmI
) => {
    const uid = getUid();
    if (!uid || !areaId || !rmId) return Promise.reject("Bad params");

    let batch = db.batch();

    const areaRef = db.collection(Collections.COMPANY_AREAS).doc(areaId);
    const rmRef = db.collection(Collections.COMPANY_RMS).doc(rmId);
    batch.set(rmRef, {...data}, {merge: true});

    batch.update(areaRef, `rms.${rmId}.name`, data.name);

    return batch.commit();
};

export const updateGame = (game: CompanyGameInterface) => {
    if (!game) return Promise.reject("Bad params");

    return db
        .collection(Collections.GAMES)
        .doc(game.id)
        .set(game, {merge: true});
};

export const addGame = (game: CompanyGameInterface) => {
    if (!game) return Promise.reject("Bad params");
    const uid: string | null = getUid();

    if (!uid) return Promise.reject("Missing uid");

    let batch = db.batch();
    const gameRef = db.collection(Collections.GAMES).doc(game.id);
    const companyRef = db.collection(Collections.USERS).doc(uid);
    const rmRef = db.collection(Collections.COMPANY_RMS).doc(game.rmId);

    batch.set(gameRef, game);
    batch.update(companyRef, {
        gameCount: firebase.firestore.FieldValue.increment(1),
    });
    batch.update(rmRef, {
        gameCount: firebase.firestore.FieldValue.increment(1),
    });

    return batch.commit();
};

export const updateGameSubmission = (submission: CompanyGameSubmissionType) => {
    if (!submission) return Promise.reject("Bad params");

    return db
        .collection(Collections.GAMES)
        .doc(submission.gameId)
        .collection(Collections.SUBMISSIONS)
        .doc(submission.studentId)
        .set(submission, {merge: true});
};

export const archiveGame = (gameId: string) => {
    if (!gameId) return Promise.reject("Bad params");

    let batch = db.batch();
    const gameRef = db.collection(Collections.GAMES).doc(gameId);
    // const companyRef = db.collection(Collections.USERS).doc(uid);
    batch.update(gameRef, "state", "ARCHIVED");
    // batch.update(companyRef, { gameCount: firebase.firestore.FieldValue.increment(-1) });

    return batch.commit();
};

// export const getGamesListener = (observer) => {
// 	const uid = getUid();
// 	if (!uid) return;

// 	return db
// 		.collection(Collections.GAMES)
// 		.where("companyId", "==", uid)
// 		.onSnapshot(observer);
// };

export const getGames = (rmId: string, last: MissingType) => {
    if (!rmId) return Promise.reject();
    let query;

    query = db
        .collection(Collections.GAMES)
        .where("rmId", "==", rmId)
        .where("state", "==", "ACTIVE")
        .orderBy("deadline", "desc");

    if (last === undefined) return query.limit(4).get();
    else return query.startAfter(last).limit(4).get();
};

export const getGamesListener = (rmId: string, cb: MissingType) => {
    if (!rmId) return Promise.reject();

    return db
        .collection(Collections.GAMES)
        .where("rmId", "==", rmId)
        .where("state", "==", "ACTIVE")
        .orderBy("deadline", "desc")
        .onSnapshot(cb);
};

export const getCompaniesWithLimit = (limit: number) => {
    return db
        .collection(Collections.USERS)
        .where("uid", ">", "")
        .limit(limit)
        .get()
        .then((snapshot) => {
            let res: CompanyInterface[] = [];
            snapshot.forEach((doc) => {
                res.push(doc.data() as CompanyInterface);
            });
            return Promise.resolve(res);
        });
};

export const getCompaniesBySector = (sector: number) => {
    return db
        .collection(Collections.USERS)
        .where("sector", "==", sector)
        .get()
        .then((snapshot) => {
            let res: CompanyInterface[] = [];
            snapshot.forEach((doc) => res.push(doc.data() as CompanyInterface));
            return Promise.resolve(res);
        })
        .catch(function (error) {
            return Promise.reject(error);
        });
};

// export const getCompaniesById = companyIds => {
// 	if (!companyIds || companyIds.length <= 0 || companyIds.length > 10)
// 		return Promise.reject("Bad params");

// 	return db.collection(Collections.USERS).where('id', 'in', companyIds)
// 		.get()
// 		.then(snapshot => {
// 			let res = [];
// 			snapshot.forEach((doc) => {
// 				res.push(doc.data());
// 			});
// 			return Promise.resolve(res);
// 		})
// }

export const getRmsByCompany = (companies: CompanyInterface[], callback: Function) => {
    if (!companies) return Promise.reject(null);

    let companyRmRefs = companies.map((company) =>
        db
            .collection(Collections.COMPANY_RMS)
            .where("companyId", "==", company.id)
            .get()
    );

    Promise.all(companyRmRefs)
        .then((snapshots) => {
            const rmsByCompany = snapshots.map((s) =>
                s.docs.map((doc) => doc.data())
            );
            callback(rmsByCompany);
        })
        .catch((e) => console.error(e));
};

export const getCompaniesById = async (companyIds: string[]) => {
    if (!companyIds) return Promise.reject(null);
    let companyRefs = companyIds.map((companyId) =>
        db.collection(Collections.USERS).doc(companyId).get()
    );

    const docs = await Promise.all(companyRefs);
    return docs.map((doc) => doc.data());
};

export const getRmsByInterests = async (interests: string[]) => {
    if (!interests || interests.length === 0) return Promise.reject([]);

    return db
        .collection(Collections.COMPANY_RMS)
        .where("tag", "in", interests)
        .where("demo", "!=", true)
        .get()
        .then((snapshot) => {
            const rms: CompanyRmI[] = snapshot.docs.map(
                (doc) => doc.data() as CompanyRmI
            );
            return rms;
        });
};

export const getRmsByArea = (areaId: string) => {
    if (!areaId) return Promise.reject([]);

    return db
        .collection(Collections.COMPANY_RMS)
        .where("areaId", "==", areaId)
        .get()
        .then((snapshot) => {
            const rms: CompanyRmI[] = snapshot.docs.map(
                (doc) => doc.data() as CompanyRmI
            );
            return rms;
        });
};
export const getAllRmsByInterests = async (interests: string[]) => {
    if (!interests || interests.length === 0) return Promise.reject([]);

    return db
        .collection(Collections.COMPANY_RMS)
        .where("tag", "in", interests)
        .get()
        .then((snapshot) => {
            const rms: CompanyRmI[] = snapshot.docs.map(
                (doc) => doc.data() as CompanyRmI
            );
            return rms;
        });
};

export const getRmsOfSpaceXByInterests = async (interests: string[]) => {
    if (!interests || interests.length === 0) return Promise.reject([]);

    return db
        .collection(Collections.COMPANY_RMS)
        .where("tag", "in", interests)
        .where("companyId", "==", "61DY1lgX02WwNmxy7jLwnA2lkCh1")
        .get()
        .then((snapshot) => {
            const rms: CompanyRmI[] = snapshot.docs.map(
                (doc) => doc.data() as CompanyRmI
            );
            return rms;
        });
};

export const getCompanies = () => {
    return db
        .collection(Collections.USERS)
        .where("role", "==", "company")
        .get()
        .then((snapshot) => {
            let res: CompanyInterface[] = [];
            snapshot.forEach((doc) => {
                res.push(doc.data() as CompanyInterface);
            });
            return Promise.resolve(res);
        });
};

export const moveAreas = async () => {
    // let batch = db.batch();
    // let snapshot = await db.collection(Collections.COMPANY_AREAS)
    // 	.where("companyId", ">", "")
    // 	.get();

    // snapshot.docs.forEach(doc => {
    // 	if (doc.exists) {
    // 		const data = doc.data();
    // 		if (data.companyId && data.id) {
    // 			const newAreaRef = db.collection(Collections.USERS).doc(data.companyId).collection(Collections.COMPANY_AREAS).doc(data.id);
    // 			batch.set(newAreaRef, data);
    // 		}
    // 	}
    // });
    // return batch.commit();
    return Promise.resolve();
};

export const moveRms = async () => {
    // let batch = db.batch();
    // let snapshot = await db.collection(Collections.COMPANY_RMS)
    // 	.where("companyId", ">", "")
    // 	.get();
    // snapshot.docs.forEach(doc => {
    // 	if (doc.exists) {
    // 		const data = doc.data();
    // 		if (data.companyId && data.id) {
    // 			const newRmRef = db
    // 			.collection(Collections.USERS)
    // 			.doc(data.companyId)
    // 			.collection(Collections.COMPANY_AREAS)
    // 			.doc(data.areaId)
    // 			.collection(Collections.COMPANY_RMS)
    // 			.doc(data.id);
    // 			batch.set(newRmRef, data);
    // 		}
    // 	}
    // });
    // return batch.commit();
    // return Promise.resolve();
};

export const sendMessage = (
    emails: string[],
    message: string,
    sender: string
) => {
    if (!emails || emails.length === 0 || !message) return Promise.reject();

    const callable = firebaseApp
        .functions("europe-west1")
        .httpsCallable("sendMessage");
    return callable({emails, message, sender});
};

export const getGameSubmissions = (gameId: string) => {
    if (!gameId) return Promise.resolve([]);

    const query = db
        .collection(Collections.GAMES)
        .doc(gameId)
        .collection(Collections.SUBMISSIONS);

    return query.get().then((snapshot) => {
        let res: CompanyGameSubmissionType[] = [];
        snapshot.forEach((doc) => {
            res.push(doc.data() as CompanyGameSubmissionType);
        });
        return res;
    });
};

export const updateGameWinners = (
    studentIds: string[],
    game: CompanyGameInterface
) => {
    if (!studentIds || !game) return Promise.reject();

    const callable = firebaseApp
        .functions("europe-west1")
        .httpsCallable("setWinners");
    return callable({winners: studentIds, gameId: game.id, rmId: game.rmId});
};

